import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass, Plus, Trash, ArrowsClockwise, Link, CheckCircle
} from '@phosphor-icons/react';

export default function CmsSeoManager() {
  const [activeTab, setActiveTab] = useState('meta'); // 'meta', 'redirects', 'sitemap'
  const [metas, setMetas] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [sitemap, setSitemap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewMeta, setShowNewMeta] = useState(false);
  const [showNewRedirect, setShowNewRedirect] = useState(false);

  useEffect(() => {
    loadMetas();
    loadRedirects();
  }, []);

  const loadMetas = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/meta`, {
        withCredentials: true,
      });
      setMetas(r.data.metas || []);
    } catch (e) {
      toast.error('Failed to load SEO meta');
    } finally {
      setLoading(false);
    }
  };

  const loadRedirects = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/redirects`, {
        withCredentials: true,
      });
      setRedirects(r.data.redirects || []);
    } catch (e) {
      toast.error('Failed to load redirects');
    }
  };

  const generateSitemap = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`);
      setSitemap(r.data);
      toast.success(`Sitemap generated: ${r.data.total} URLs`);
    } catch (e) {
      toast.error('Failed to generate sitemap');
    }
  };

  const deleteMeta = async (metaId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/meta/${metaId}`, {
        withCredentials: true,
      });
      toast.success('SEO meta deleted');
      loadMetas();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const deleteRedirect = async (redirectId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/redirects/${redirectId}`, {
        withCredentials: true,
      });
      toast.success('Redirect deleted');
      loadRedirects();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-seo-manager">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <MagnifyingGlass size={28} weight="duotone" className="text-primary" />
          SEO & Redirects Manager
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Optimize meta tags, manage 301/302 redirects, and generate XML sitemaps
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-border">
        {['meta', 'redirects', 'sitemap'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize ${
              activeTab === tab
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'meta' ? 'Meta Tags' : tab === 'redirects' ? 'Redirects' : 'Sitemap'}
          </button>
        ))}
      </div>

      {/* Meta Tags Tab */}
      {activeTab === 'meta' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{metas.length} pages configured</p>
            <button
              onClick={() => setShowNewMeta(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-semibold"
              data-testid="new-meta-btn"
            >
              <Plus size={14} /> Add Meta Tags
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : metas.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground">No SEO meta tags configured yet</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {metas.map((meta) => (
                <div key={meta.meta_id} className="p-4" data-testid={`meta-${meta.meta_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold">{meta.page_path}</p>
                      {meta.title && <p className="text-sm text-muted-foreground mt-1">Title: {meta.title}</p>}
                      {meta.description && (
                        <p className="text-xs text-muted-foreground mt-1">Description: {meta.description.slice(0, 100)}...</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteMeta(meta.meta_id)}
                      className="text-muted-foreground hover:text-red-600 p-1"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Redirects Tab */}
      {activeTab === 'redirects' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{redirects.length} redirects configured</p>
            <button
              onClick={() => setShowNewRedirect(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-semibold"
              data-testid="new-redirect-btn"
            >
              <Plus size={14} /> Add Redirect
            </button>
          </div>

          {redirects.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground">No redirects configured yet</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {redirects.map((redirect) => (
                <div key={redirect.redirect_id} className="p-4" data-testid={`redirect-${redirect.redirect_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded">
                        {redirect.redirect_type}
                      </span>
                      <div className="flex items-center gap-2 text-sm">
                        <code className="bg-muted px-2 py-1 rounded">{redirect.from_path}</code>
                        <span>→</span>
                        <code className="bg-muted px-2 py-1 rounded">{redirect.to_path}</code>
                      </div>
                      <span className="text-xs text-muted-foreground">{redirect.hit_count} hits</span>
                    </div>
                    <button
                      onClick={() => deleteRedirect(redirect.redirect_id)}
                      className="text-muted-foreground hover:text-red-600 p-1"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sitemap Tab */}
      {activeTab === 'sitemap' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {sitemap ? `${sitemap.total} URLs in sitemap` : 'No sitemap generated yet'}
            </p>
            <button
              onClick={generateSitemap}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-semibold"
              data-testid="generate-sitemap-btn"
            >
              <ArrowsClockwise size={14} /> Generate Sitemap
            </button>
          </div>

          {sitemap && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={20} weight="fill" className="text-green-600" />
                <p className="font-semibold">Sitemap Generated Successfully</p>
              </div>
              <div className="bg-background rounded p-3 text-xs space-y-1 max-h-96 overflow-y-auto">
                {sitemap.urls.slice(0, 20).map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Link size={12} className="text-muted-foreground" />
                    <code>{url.loc}</code>
                    <span className="text-muted-foreground">({url.changefreq})</span>
                  </div>
                ))}
                {sitemap.urls.length > 20 && (
                  <p className="text-muted-foreground italic">...and {sitemap.urls.length - 20} more URLs</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
